/* card-fp: 卡片明文折后价（首页/集合/PDP推荐位）。与 PDP v-fp 独立。明文 sale-style，无 canvas、无交互门。
   spec: docs/superpowers/specs/2026-06-26-card-final-price-design.md
   v2（2026-06-27）：立即触发（去 idle 等待）+ 批量回来即显示 + localStorage 缓存（刷新瞬间显示，后台静默校正）。
   v3（2026-07-20）：多币种（参照 v-fp v3）——PRICE_RE 去 $ 锚定（€/£/¥ + 欧式分隔符）；token 解析改
   「最后的 [.,] 且后跟 1-2 位数字者为小数点」；折后价/Save 金额用主题 moneyFormat 的分隔符与货币符渲染；
   cartCreate 探测补 @inContext(country)（themeVariables.localizationCountry → localization cookie 兜底）；
   localStorage 键加国家/币种维度（cardfp:2:<ctx>，清除旧 cardfp:1）；探测币种≠页面币种按失败处理，宁缺勿错。
   渲染幂等可逆：同 save 不动 DOM（不闪、不触发 observer 死循环）；save 变了先还原再应用；save<=0 还原原价。 */
(function () {
  'use strict';

  // 数字段（NUM_SRC）：首尾必须是数字，中间允许 , . ' 空格/NBSP 分隔符——PRICE_RE 与 finalFromToken 的
  // 替换正则共用同一来源，保证「匹配到的数字」和「被替换的数字」永远是同一段。
  var NUM_SRC = "\\d(?:[\\d.,\\u00A0\\u202F' ]*\\d)?";
  var PRICE_RE = new RegExp("[$\\u20AC\\u00A3\\u00A5]\\s?" + NUM_SRC + "(?:\\s*[A-Za-z]{3})?");   // $ € £ ¥
  var NUM_RE = new RegExp(NUM_SRC);

  // 一个 cartCreate 响应 -> { present:{vid:save}, returned:{vid:true}, cur }，按 merchandise.id 归位。
  // save 以响应自身币种计（v3 起探测带 @inContext(country)，即页面币种）；cur=响应币种，供页面侧校验。
  function parseSaves(cartJson) {
    var out = { present: {}, returned: {}, cur: null };
    var cc = cartJson && cartJson.data && cartJson.data.cartCreate;
    var cart = cc && cc.cart;
    var edges = cart && cart.lines && cart.lines.edges ? cart.lines.edges : [];
    for (var i = 0; i < edges.length; i++) {
      var node = edges[i] && edges[i].node;
      if (!node || !node.merchandise || !node.merchandise.id) continue;
      var vid = String(node.merchandise.id).split('/').pop();
      if (!out.cur && node.cost && node.cost.subtotalAmount && node.cost.subtotalAmount.currencyCode) out.cur = node.cost.subtotalAmount.currencyCode;
      var save = 0;
      var allocs = node.discountAllocations || [];
      for (var j = 0; j < allocs.length; j++) {
        var a = allocs[j];
        if (a && a.targetType === 'LINE_ITEM' && a.discountedAmount) {
          save += parseFloat(a.discountedAmount.amount);
          if (!out.cur && a.discountedAmount.currencyCode) out.cur = a.discountedAmount.currencyCode;
        }
      }
      out.returned[vid] = true;
      out.present[vid] = save > 0.009 ? save : 0;
    }
    return out;
  }

  // token 数字解析（币种感知，同 v-fp v3 parsePrice 语义 + 千分位守卫）：最后出现的 [.,] 且后跟 1-2 位
  // 数字者为小数点，其余全是千分位。"€210,95"→210.95  "$1,199.99"→1199.99  "€1.234,56"→1234.56
  // "$1,199"/"¥1,199"（后跟 3 位）→千分位→1199。返回 { value, dec }（dec=token 自带的小数位数）。
  function splitNum(token) {
    var s = String(token).replace(/[^\d.,]/g, '');
    if (!s) return null;
    var li = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    var frac = li >= 0 ? s.length - li - 1 : 0;
    var dec = (frac === 1 || frac === 2) ? frac : 0;
    var n = parseInt(s.replace(/[.,]/g, ''), 10);
    if (!isFinite(n)) return null;
    return { value: dec > 0 ? n / Math.pow(10, dec) : n, dec: dec };
  }

  var MONEY_STYLES = {   // moneyFormat 占位符 -> [千分位, 小数符, (存在第 3 项=强制 0 小数)]（与 v-fp v3 同源）
    amount: [',', '.'], amount_no_decimals: [',', '.', 0],
    amount_with_comma_separator: ['.', ','], amount_no_decimals_with_comma_separator: ['.', ',', 0],
    amount_with_space_separator: [' ', '.'], amount_no_decimals_with_space_separator: [' ', '.', 0],
    amount_with_apostrophe_separator: ["'", '.'], amount_no_decimals_with_apostrophe_separator: ["'", '.', 0]
  };

  // 分隔符 [千分位, 小数符]：优先主题 moneyFormat（js-variables.liquid 暴露，随币种变）；
  // node 单测/themeVariables 缺失时从 token 自身反推（token 本来就是主题按当前币种渲染的）。
  function moneySeps(token) {
    try {
      var tv = typeof window !== 'undefined' && window.themeVariables;
      var fmt = tv && tv.settings && tv.settings.moneyFormat;
      var m = fmt ? String(fmt).match(/\{\{\s*(\w+)\s*\}\}/) : null;
      if (m && MONEY_STYLES[m[1]]) return MONEY_STYLES[m[1]];
    } catch (e) { /* fall through */ }
    var grouped = String(token).match(/\d([ '\u00A0\u202F])\d{3}(?:[.,]\d{1,2})?(?:\D|$)/);
    var groupMark = grouped ? grouped[1] : null;
    var s = String(token).replace(/[^\d.,]/g, '');
    var li = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (li < 0) return groupMark ? [groupMark, '.'] : [',', '.'];
    var frac = s.length - li - 1;
    var mark = s.charAt(li);
    if (frac === 1 || frac === 2) return groupMark ? [groupMark, mark] : (mark === ',' ? ['.', ','] : [',', '.']);   // mark 是小数符
    if (groupMark) return [groupMark, '.'];
    return mark === '.' ? ['.', ','] : [',', '.'];                                 // mark 是千分位
  }

  function formatNum(n, dec, seps) {
    var parts = n.toFixed(dec).split('.');
    var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + seps[0]);
    return head + (parts[1] ? seps[1] + parts[1] : '');
  }

  // DOM lead 价格文本 + 折扣额 -> { token, finalToken } 或 null（只替换数字，继承货币符/ISO 后缀/格式风格）。
  function finalFromToken(priceText, save) {
    if (!priceText) return null;
    var m = String(priceText).match(PRICE_RE);
    if (!m) return null;
    var token = m[0];
    var p = splitNum(token);
    if (!p || !(p.value > 0)) return null;
    var finalNum = p.value - save;
    if (!(finalNum > 0)) return null;
    var finalToken = token.replace(NUM_RE, formatNum(finalNum, p.dec, moneySeps(token)));
    return { token: token, finalToken: finalToken };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseSaves: parseSaves, finalFromToken: finalFromToken, splitNum: splitNum, PRICE_RE: PRICE_RE };
  }
  if (typeof document === 'undefined') return;   // node/test 环境到此为止，不跑浏览器逻辑

  if (window.__cardfpInit) return;
  window.__cardfpInit = true;

  var cfgEl = document.getElementById('card-fp-config');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.api) return;

  var API = location.origin + '/api/' + cfg.api + '/graphql.json';
  // v3：探测带 @inContext(country) —— 不带则永远按店铺默认市场（USD）计价/计折扣，非美元站点全错。
  // 国家码来源：① themeVariables.localizationCountry（本地仓库 js-variables.liquid 有、2026-07-20 线上还没有）；
  // ② Shopify 官方 localization cookie（买家选过地区/被地理定位后必有）。都取不到 -> null（店铺默认市场），
  // 此时若页面是非默认币种，cart() 的币种守卫会拦下错币折扣额，安全失败不乱渲染。
  var TV = (window.themeVariables && window.themeVariables.settings) || {};
  function cookieCountry() {
    try {
      var m = String(document.cookie).match(/(?:^|;\s*)localization=([A-Z]{2})(?:;|$)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }
  var COUNTRY = TV.localizationCountry || cookieCountry() || null;
  var PAGE_CUR = TV.cartCurrency || null;
  var QUERY = 'mutation P($lines:[CartLineInput!]!,$country:CountryCode)@inContext(country:$country){cartCreate(input:{lines:$lines}){cart{lines(first:50){edges{node{merchandise{... on ProductVariant{id}}cost{subtotalAmount{amount currencyCode}totalAmount{amount}}discountAllocations{discountedAmount{amount currencyCode}targetType}}}}}userErrors{message}}}';
  var SEL = '.price-list[data-cardfp-variant]';
  var POOL = 6;
  // v3：缓存键加国家/币种维度（折扣额随市场不同），防切币种后串用旧币种折扣额；顺手清掉旧无维度键。
  var LS_KEY = 'cardfp:2:' + (COUNTRY || PAGE_CUR || 'US');
  try { localStorage.removeItem('cardfp:1'); } catch (e) {}
  var LS_TTL = 2 * 60 * 60 * 1000;   // 2 小时（价格变动不频繁，延长缓存以更长时间扛住限流）
  var FRESH_DISCOUNT = 0;             // 有折扣变体：0=每次加载仍重探测（规避促销下线后显旧价）
  var FRESH_ZERO = 15 * 60 * 1000;    // 无折扣变体：15min 内跳过重探测（减少 cartCreate、降限流）

  var cache = new Map();          // vid -> save（页面币种折扣额，本页内存权威）
  var probed = new Set();         // 本页已网络探测过的 vid（避免 observer 重入重复请求）
  var running = false, rerunQueued = false;
  var mo = null, moT = null;

  // ---- localStorage 缓存（明文卡片，缓存折扣额以便刷新/翻页瞬间显示）----
  function validLs(e) { return e && typeof e === 'object' && isFinite(e.s) && e.s >= 0 && isFinite(e.t) && e.t > 0; }
  function lsPrune(o, now) { Object.keys(o).forEach(function (v) { var e = o[v]; if (!validLs(e) || (now - e.t) >= LS_TTL) delete o[v]; }); return o; }
  function lsFresh(e, now) { if (!validLs(e)) return false; var ttl = e.s > 0 ? FRESH_DISCOUNT : FRESH_ZERO; return ttl > 0 && (now - e.t) < ttl; }
  function lsRead() {
    try { var o = JSON.parse(localStorage.getItem(LS_KEY) || '{}'); return lsPrune((o && typeof o === 'object') ? o : {}, Date.now()); } catch (e) { return {}; }
  }
  function lsWrite(o) { try { localStorage.setItem(LS_KEY, JSON.stringify(lsPrune(o, Date.now()))); } catch (e) {} }

  function cart(vids) {
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, 5000);
    var headers = { 'Content-Type': 'application/json' };
    if (cfg.k) headers['X-Shopify-Storefront-Access-Token'] = cfg.k;
    return fetch(API, {
      method: 'POST', headers: headers, signal: ctrl.signal,
      body: JSON.stringify({ query: QUERY, variables: { lines: vids.map(function (v) { return { merchandiseId: 'gid://shopify/ProductVariant/' + v, quantity: 1 }; }), country: COUNTRY } })
    }).then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        if (!(json && json.data && !(json.errors && json.errors.length))) return null;   // 限流/GraphQL 错误(200+errors,data:null)当失败处理 -> 保留缓存,绝不撤销
        var cur = parseSaves(json).cur;
        if (cur && PAGE_CUR && cur !== PAGE_CUR) return null;   // v3：探测币种≠页面币种（country 未生效/快照陈旧）-> 按失败处理，宁缺勿错
        return json;
      })
      .catch(function () { return null; })
      .finally(function () { clearTimeout(timer); });
  }

  function pool(items, n, worker) {
    var i = 0, active = 0;
    return new Promise(function (resolve) {
      function next() {
        if (i >= items.length && active === 0) return resolve();
        while (active < n && i < items.length) {
          var it = items[i++]; active++;
          Promise.resolve(worker(it)).catch(function () {}).then(function () { active--; next(); });
        }
      }
      next();
    });
  }

  function chunk(arr, n) { var out = []; for (var i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; }

  function findLead(pl) {
    return pl.querySelector('.price--highlight') || pl.querySelector('.price:not(.price--compare):not(.price--block)');
  }

  // v3：Save 金额按主题 moneyFormat 完整渲染（含货币符，"Save €70"/"Save $70"），不再硬编码 $。
  // trimWhole：整数金额去小数（Save €70 而非 Save €70,00）。moneyFormat 缺失时退 Intl（页面币种），再退 $。
  function money(n, trimWhole) {
    var whole = trimWhole && Math.abs(n - Math.round(n)) < 0.005;
    try {
      var fmt = TV.moneyFormat;
      var m = fmt ? String(fmt).match(/\{\{\s*(\w+)\s*\}\}/) : null;
      var style = m && MONEY_STYLES[m[1]];
      if (style) {
        var dec = style.length > 2 ? 0 : (whole ? 0 : 2);
        return String(fmt).replace(/<[^>]*>/g, '').replace(m[0], formatNum(n, dec, style)).trim();
      }
    } catch (e) { /* fall through */ }
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: PAGE_CUR || 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(n);
    } catch (e) { return '$' + n.toFixed(2); }
  }

  // 幂等可逆渲染。pl._cardfpSave=当前已渲染的 save 指纹；pl._cardfpApplied=是否已应用折后；pl._cardfpOrig=原始 lead HTML。
  function renderCard(pl, save) {
    try {
      save = save > 0 ? save : 0;
      if (pl._cardfpSave === save) return;              // 已是目标状态 -> 零 DOM 改动（防闪/防 observer 死循环）
      var lead = findLead(pl);
      if (!lead) return;
      if (pl._cardfpApplied) {                          // 撤销之前的折后渲染，回到原价（折扣变更/失效时）
        lead.innerHTML = pl._cardfpOrig;
        var extras = pl.querySelectorAll('.card-fp-was, .card-fp-save');   // 移除补的划线原价 + Save chip
        for (var ei = 0; ei < extras.length; ei++) extras[ei].remove();
        pl._cardfpApplied = false;
      }
      pl._cardfpSave = save;
      if (!(save > 0)) return;                          // 无折扣：保持原价（从未应用过则一字未动）
      if (pl._cardfpOrig == null) pl._cardfpOrig = lead.innerHTML;   // 首次记下原始 lead，用于将来还原
      var vh = lead.querySelector('.visually-hidden');
      var priceText = '';
      lead.childNodes.forEach(function (nn) {
        if (!(nn.nodeType === 1 && nn.classList && nn.classList.contains('visually-hidden'))) priceText += nn.textContent;
      });
      priceText = priceText.trim();
      var r = finalFromToken(priceText, save);
      if (!r) return;
      lead.innerHTML = (vh ? vh.outerHTML : '') + priceText.replace(r.token, '<span class="card-fp-final">' + r.finalToken + '</span>');
      if (!pl.querySelector('.price--compare')) {
        var s = document.createElement('span');
        s.className = 'price price--compare card-fp-was';
        s.innerHTML = '<span class="visually-hidden">Regular price</span>' + r.token;
        lead.insertAdjacentElement('afterend', s);
      }
      var chip = document.createElement('span');           // 克制的折扣信号：Save €/$XX（金额=折扣额，整数去小数）
      chip.className = 'card-fp-save';
      chip.textContent = 'Save ' + money(save, true);
      var anchor = pl.querySelector('.card-fp-was') || pl.querySelector('.price--compare') || lead;   // 放在划线原价之后
      anchor.insertAdjacentElement('afterend', chip);
      pl._cardfpApplied = true;
    } catch (e) { /* never throw into the page */ }
  }

  function renderVid(byVid, vid, save) {
    cache.set(vid, save);
    (byVid.get(vid) || []).forEach(function (el) { renderCard(el, save); });
  }

  async function run() {
    if (running) { rerunQueued = true; return; }
    running = true;
    try {
      var els = Array.from(document.querySelectorAll(SEL)).filter(function (el) { return el.getAttribute('data-cardfp-variant'); });
      if (els.length) {
        var byVid = new Map();
        els.forEach(function (el) { var v = el.dataset.cardfpVariant; if (!byVid.has(v)) byVid.set(v, []); byVid.get(v).push(el); });
        var now = Date.now(); var lsAcc = lsRead();

        // 1) 瞬间渲染：localStorage 命中（新鲜、有折扣）或内存已知 -> 立即画（刷新/翻页不等网络）
        byVid.forEach(function (list, vid) {
          if (!cache.has(vid)) { var e = lsAcc[vid]; if (validLs(e) && e.s > 0 && (now - e.t) < LS_TTL) cache.set(vid, e.s); }
          if (cache.has(vid)) list.forEach(function (el) { renderCard(el, cache.get(vid)); });
        });

        // 2) 网络复核：本页未探测过的 vid
        var toProbe = Array.from(byVid.keys()).filter(function (v) { return !probed.has(v) && !lsFresh(lsAcc[v], now); });
        if (toProbe.length) {
          toProbe.forEach(function (v) { probed.add(v); });
          var candidates = [], rechecked = {};
          // Stage 1 批量：按 merchandise.id 归位，回来即渲染（快）；缺失 vid 进 recheck 不整块置 0
          await pool(chunk(toProbe, 50), POOL, function (block) {
            return cart(block).then(function (json) {
              var parsed = parseSaves(json || {});
              block.forEach(function (v) {
                if (parsed.returned[v]) {
                  var s = parsed.present[v] || 0;
                  if (s > 0) { candidates.push(v); lsAcc[v] = { s: s, t: Date.now() }; } else { lsAcc[v] = { s: 0, t: Date.now() }; }
                  renderVid(byVid, v, s);
                } else { rechecked[v] = true; }
              });
            });
          });
          lsWrite(lsAcc);
          // Stage 2 单 line 隔离确认：后台静默校正（= 单买结账真值）；renderCard 仅在值变了才动 DOM
          var stage2 = candidates.concat(Object.keys(rechecked));
          if (stage2.length) {
            await pool(stage2, POOL, function (v) {
              return cart([v]).then(function (json) {
                if (json == null) return;                  // 请求失败/超时/无响应 -> 未知，保留当前(缓存)值，绝不撤销
                var cc = json.data && json.data.cartCreate;
                var s;
                if (!cc || (cc.userErrors && cc.userErrors.length) || !cc.cart) s = 0;   // 有响应但无 cart/userErrors -> 确定无折扣
                else { var parsed = parseSaves(json); s = parsed.returned[v] ? (parsed.present[v] || 0) : 0; }
                if (s > 0) lsAcc[v] = { s: s, t: Date.now() }; else lsAcc[v] = { s: 0, t: Date.now() };
                renderVid(byVid, v, s);
              });
            });
            lsWrite(lsAcc);
          }
        }
      }
    } catch (e) { /* never throw */ }
    finally {
      running = false;
      if (rerunQueued) { rerunQueued = false; run(); }
    }
  }

  function schedule() { clearTimeout(moT); moT = setTimeout(run, 200); }

  function init() {
    run();                                              // 立即开始（不再等 requestIdleCallback；script 已 defer）
    mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
